import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { Fragment } from 'react';

import { LineType } from 'features/seismic/models/enums/LineType';
import { SeismicAttribute } from 'features/seismic/models/interfaces/ISurveyMetadata';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { SampleIntervalUnit } from 'features/seismic/models/enums/SampleIntervalUnity';

export function SeismicAttributesContent({ seismicAttributes, closePopup }: Props) {
    const { lineNumber, setLineNumber, lineType } = useLine3DNavigationStore(state => ({
        lineNumber: state.lineNumber,
        setLineNumber: state.setLineNumber,
        lineType: state.lineType
    }));

    const { surveyMetadata } = useSeismicStore(state => ({
        surveyMetadata: state.surveyMetadata,
    }));

    function getClosestLine(startProduct: number, endProduct: number, incrementProduct: number, line: number, lineIncrement: number | undefined) {
        let productLinesMatched = getMatchedLines(startProduct, endProduct, incrementProduct, line, lineIncrement) as number[];
        let minDistance = 0;
        let closestLine = 0;
        for (let i = 0; i < productLinesMatched.length; i++) {
            let distance = Math.abs(lineNumber - productLinesMatched[i]);
            if (i === 0) {
                minDistance = distance;
                closestLine = productLinesMatched[i];
                break;
            } else {
                if (distance < minDistance) {
                    minDistance = distance;
                    closestLine = productLinesMatched[i];
                }
            }
        }

        return closestLine;
    }

    function getClosestLineControl(startProduct: number, endProduct: number, incrementProduct: number, line: number, lineIncrement: number | undefined) {
        let closestLine = getClosestLine(startProduct, endProduct, incrementProduct, line, lineIncrement);
        if (closestLine === 0) {
            return 'N/A';
        } else {
            return closestLine.toString();
        }
    }

    function getClosestLineColor(startProduct: number, endProduct: number, incrementProduct: number, line: number, lineIncrement: number | undefined) {
        let closestLine = getClosestLine(startProduct, endProduct, incrementProduct, line, lineIncrement);
        if (closestLine === 0) {
            return 'rgb(199 100 100)';
        } else {
            return '#011045';
        }
    }

    function getMatchedLines(startProduct: number, endProduct: number, incrementProduct: number, line: number, lineIncrement: number | undefined) {
        if (surveyMetadata) {
            let mainLines: number[] = [];
            if (lineType === LineType.Inline) {
                for (let i = surveyMetadata.Survey3DInfo.InlineStart; i <= surveyMetadata.Survey3DInfo.InlineEnd; i += surveyMetadata.Survey3DInfo.InlineIncrement) {
                    mainLines.push(i);
                }
            } else if (lineType === LineType.Xline) {
                for (let i = surveyMetadata.Survey3DInfo.XlineStart; i <= surveyMetadata.Survey3DInfo.XlineEnd; i += surveyMetadata.Survey3DInfo.XlineIncrement) {
                    mainLines.push(i);
                }
            }

            let productLines = [];
            if (incrementProduct !== 0) {
                for (let i = startProduct; i <= endProduct; i += incrementProduct) {
                    productLines.push(i);
                }
            }

            let productLinesMatched = productLines.filter((x) => mainLines.includes(x));
            return productLinesMatched;
        }
    }

    function getClosestLineOld(startProduct: number, endProduct: number, incrementProduct: number, line: number, lineIncrement: number | undefined) {
        if (lineIncrement) {
            if (surveyMetadata?.Survey3DInfo?.XlineStart && surveyMetadata?.Survey3DInfo?.InlineStart) {
                if (endProduct <= surveyMetadata?.Survey3DInfo?.XlineStart || startProduct >= surveyMetadata?.Survey3DInfo?.XlineEnd) {
                    return 0;
                }
            } else {
                return 0;
            }

            let productLines = [];
            for (let i = startProduct; i <= endProduct; i += incrementProduct) {
                productLines.push(i);
            }

            // let closestLine = productLines.filter((x) => x >= line);
            // return closestLine[0];

            let increment = 0;
            let higherIncrement = incrementProduct >= lineIncrement ? incrementProduct : lineIncrement;
            let lowerIncrement = incrementProduct <= lineIncrement ? incrementProduct : lineIncrement;

            if (higherIncrement % lowerIncrement === 0) {
                increment = higherIncrement;
            }
            else {
                increment = higherIncrement * lowerIncrement;
            }

            if (line > endProduct) {
                if (line % increment !== 0) {
                    return endProduct - increment;
                }
                return endProduct;
            }

            if (line < startProduct) {
                if (line % increment !== 0) {
                    return startProduct + increment;
                }
                return startProduct;
            }
            return line - increment;
        }
        return 0;
    }

    let lineTypeLabel = '-';
    let lineNumberLabel = '';
    let is3D = false;

    if (lineType === LineType.Inline) {
        lineTypeLabel = 'Inline';
        lineNumberLabel = ' - ' + lineNumber;
        is3D = true;
    }
    else if (lineType === LineType.Xline) {
        lineTypeLabel = 'Xline';
        lineNumberLabel = ' - ' + lineNumber;
        is3D = true;
    }
    else if (lineType === LineType.ZSlice) {
        lineTypeLabel = '';
        lineNumberLabel = lineNumber.toString();
        is3D = true;
    }
    else if (lineType === LineType.Line2D) {
        lineTypeLabel = '2D';
    }

    console.log('SEIS ATTR =============>', seismicAttributes);

    return (
        <Box style={{ padding: '20px', width:'100%' }}>
            {seismicAttributes &&
                <Stack gap={1}>
                    {surveyMetadata && !!seismicAttributes.baseProductName &&
                        <Box>
                            <Stack direction='row' gap={1}><Typography fontWeight='bold'>Selected product:</Typography><Typography>{seismicAttributes.baseProductName}</Typography></Stack>
                            <Stack direction='row' gap={1}><Typography fontWeight='bold'>{ lineType === LineType.ZSlice ? 'Depth:' : 'Line:'}</Typography><Typography>{lineTypeLabel}{lineNumberLabel}</Typography></Stack>
                            <hr></hr>
                        </Box>
                    }
                    <Typography variant='h6'>Available Products</Typography>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell align='left'>Type</TableCell>
                                    <TableCell align='left'>Product</TableCell>
                                    <TableCell align='center'>Samples per Trace</TableCell>
                                    <TableCell align='center'>Sample Interval (ms)</TableCell>
                                    {!is3D && <TableCell align='center'>Total of Traces</TableCell>}
                                    {is3D && <Fragment>
                                        <TableCell align='center'>IL Start</TableCell>
                                        <TableCell align='center'>IL Inc</TableCell>
                                        <TableCell align='center'>IL End</TableCell>
                                        <TableCell align='center'>XL Start</TableCell>
                                        <TableCell align='center'>XL Inc</TableCell>
                                        <TableCell align='center'>XL End</TableCell>
                                    </Fragment>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {seismicAttributes.all.map((attribute) => (
                                    <TableRow sx={attribute.Product === seismicAttributes.baseProductName ? { backgroundColor: '#e3e2e1', width: '100%' } : {width: '100%'}}
                                        key={attribute.Product}
                                    // sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                    >
                                        <TableCell>{attribute.VolumeName}</TableCell>
                                        <TableCell>{attribute.Product}</TableCell>
                                        <TableCell align='center'>{attribute.SamplesPerTrace}</TableCell>
                                        <TableCell align='center'>{attribute.SampleIntervalUnit === SampleIntervalUnit.Microsecond ? attribute.SampleInterval / 1000 : attribute.SampleInterval}ms</TableCell>
                                        {!is3D && <TableCell align='center'>{attribute.Survey3DInfo.XlineEnd - attribute.Survey3DInfo.XlineStart}</TableCell>}
                                        {is3D && <Fragment>
                                            <TableCell align='center'>{attribute.Survey3DInfo.InlineStart}</TableCell>
                                            <TableCell align='center'>{attribute.Survey3DInfo.InlineIncrement}</TableCell>
                                            <TableCell align='center'>{attribute.Survey3DInfo.InlineEnd}</TableCell>
                                            <TableCell align='center'>{attribute.Survey3DInfo.XlineStart}</TableCell>
                                            <TableCell align='center'>{attribute.Survey3DInfo.XlineIncrement}</TableCell>
                                            <TableCell align='center'>{attribute.Survey3DInfo.XlineEnd}</TableCell>
                                            {/*<TableCell align='center'>
                                                <Box style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                                    {lineType === LineType.Inline &&
                                                        <Box
                                                            style={{ color: '#ffffff', backgroundColor: getClosestLineColor(attribute.Survey3DInfo.InlineStart, attribute.Survey3DInfo.InlineEnd, attribute.Survey3DInfo.InlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.InlineIncrement), borderRadius: '5px', cursor: 'pointer', width: 'fit-content', padding: '5px' }}
                                                            onClick={() => {
                                                                let matchedLine = getClosestLine(attribute.Survey3DInfo.InlineStart, attribute.Survey3DInfo.InlineEnd, attribute.Survey3DInfo.InlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.InlineIncrement);
                                                                if (matchedLine > 0) {
                                                                    setLineNumber(matchedLine);
                                                                    closePopup();
                                                                }
                                                            }
                                                            }>
                                                            {getClosestLineControl(attribute.Survey3DInfo.InlineStart, attribute.Survey3DInfo.InlineEnd, attribute.Survey3DInfo.InlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.InlineIncrement)}
                                                        </Box>
                                                    }
                                                    {lineType === LineType.Xline &&
                                                        <Box
                                                            style={{ backgroundColor: getClosestLineColor(attribute.Survey3DInfo.XlineStart, attribute.Survey3DInfo.XlineEnd, attribute.Survey3DInfo.XlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.InlineIncrement), borderRadius: '5px', cursor: 'pointer', width: 'fit-content', padding: '5px', color: '#ffffff' }}
                                                            onClick={() => {
                                                                let matchedLine = getClosestLine(attribute.Survey3DInfo.XlineStart, attribute.Survey3DInfo.XlineEnd, attribute.Survey3DInfo.XlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.InlineIncrement);
                                                                if (matchedLine > 0) {
                                                                    setLineNumber(matchedLine);
                                                                    closePopup();
                                                                }
                                                            }}>
                                                            {getClosestLineControl(attribute.Survey3DInfo.XlineStart, attribute.Survey3DInfo.XlineEnd, attribute.Survey3DInfo.XlineIncrement, lineNumber, surveyMetadata?.Survey3DInfo?.XlineIncrement)}
                                                        </Box>
                                                    }
                                                    {lineType !== LineType.Inline && lineType !== LineType.Xline &&
                                                        <Typography>-</Typography>
                                                    }
                                                </Box>
                                            </TableCell>*/}
                                        </Fragment>}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            }
        </Box>
    );
}

interface Props {
    seismicAttributes: {
        baseProductName: string,
        all: SeismicAttribute[]
    } | undefined;
    closePopup: () => void;
}